/**
 * Phase 5 (2026-05-25) — Centralized help-copy registry.
 *
 * Single source of truth for hover tooltip text across the Repancy SPA.
 * Pairs with — does not replace — the long-form `src/topics/*.md`
 * topic docs (those still power HelpWindow + the /learn routes).
 *
 * Authoring contract (REQUIRED for every entry in the top-30 sweep):
 *   1. Open the component file. Read the JSX, the click handler, every
 *      dispatched Redux action, every Convex mutation/action/query,
 *      every side effect.
 *   2. Write `short` (≤140 chars, plain English, attorney-readable)
 *      describing what *actually* happens on click — not what the
 *      button "should" do.
 *   3. Optionally add `long` (ReactNode | string) for two-paragraph
 *      explainers; renders in the bubble behind a "Learn more" chevron.
 *   4. Fill `verified.{component, handler, triggers[]}` accurately.
 *      `triggers` strings have shape:
 *        - `Redux:<sliceName>/<actionType>`  (matches createSlice action)
 *        - `Convex:<moduleBaseName>.<fnName>` (matches an exported
 *           query/mutation/action under /convex)
 *        - `Window:open <path>` for WindowContext open/focus calls
 *        - `Nav:<route>` for TanStack Router navigations (informational)
 *        - `Browser:<api>` for direct browser API calls (window.open,
 *          localStorage, history.push, setState, etc.) — informational
 *        - Use `Browser:setState` for buttons that only flip local
 *          component state (e.g. opening a modal that's controlled by
 *          a `useState` boolean within the same component).
 *   5. Run `pnpm vitest run src/lib/help/__tests__/copyTruth.test.ts`
 *      → MUST pass. The source-grep contract asserts that `component`
 *      and `handler` exist; if a refactor renames the handler the
 *      test fails until copy is re-verified against new behavior.
 *
 * Voice / audience reminder:
 *   - Audience = attorneys familiar with Harvey.ai-class AI legal tools
 *     but NOT software developers.
 *   - AVOID: "Convex", "MSAL", "demoSessionId", "tenantBranding",
 *     "Sites.Selected", "/admin/", function names, route paths.
 *   - PREFER: "your portal", "Microsoft sign-in", "your firm",
 *     "sample workspace", "real document".
 */
import type { ReactNode } from 'react';

export interface HelpCopyEntry {
  short: string;
  long?: ReactNode | string;
  /**
   * Truth-contract metadata. Required for every entry shipped in the
   * Phase 5 top-30 sweep. Enforced by `copyTruth.test.ts`.
   */
  verified: {
    /** Repo-relative path to the file that owns the handler. */
    component: string;
    /**
     * Name of the click/keydown handler function or arrow-function
     * binding inside `component`. Grep-friendly (no whitespace).
     */
    handler: string;
    /**
     * What the handler does, expressed as one or more trigger strings
     * (Redux:*, Convex:*, Window:*, Nav:*, Browser:*).
     */
    triggers: ReadonlyArray<string>;
  };
}

/**
 * The registry itself. Adding a new entry:
 *   1. Pick a stable key in dot-notation: `<surface>.<group>.<button>`.
 *      e.g. `explorer.toolbar.up`, `documentPreview.openInSharePoint.disabled`.
 *   2. Write `short` + optional `long` per the authoring contract above.
 *   3. Fill `verified` per the same contract.
 *   4. Run the copyTruth + dead-key tests.
 *
 * Entries are added as the top-30 sweep proceeds (tasks 5.5.1 through 5.5.7).
 * The registry starts empty; tests are tolerant of an empty registry so
 * the foundation can land in its own commit ahead of the sweep.
 */
export const helpCopy = {
  // ---- 5.5.2 Explorer toolbar -----------------------------------------
  'explorer.toolbar.back': {
    short: 'Go back to the previous folder you opened.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'goBack',
      triggers: ['Browser:navHistory.pop'],
    },
  },
  'explorer.toolbar.forward': {
    short: 'Go forward to the folder you just came back from.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'goForward',
      triggers: ['Browser:navHistory.push'],
    },
  },
  'explorer.toolbar.up': {
    short: 'Move up one level — from a matter back to the client folder, or from a client back to the firm.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'goUp',
      triggers: ['Browser:navHistory.push'],
    },
  },
  'explorer.toolbar.importFromSharePoint': {
    short: 'Link existing SharePoint files into the selected matter without copying them.',
    long:
      'Opens the Import from SharePoint wizard. Pick a site, then a library, then the files you want — Repancy stores a reference (and a snapshot) so the file stays editable in SharePoint and stays searchable here.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'setWizardOpen',
      triggers: ['Browser:setState'],
    },
  },
  'explorer.toolbar.bulkImport': {
    short: 'Migrate a folder of files from SharePoint into the selected matter, all at once.',
    long:
      'Bulk import runs in the background. You\'ll see progress in the "Imports running" panel, and each imported file becomes a real document in the matter — versioned, searchable, and openable in SharePoint.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'setBulkWizardOpen',
      triggers: ['Browser:setState'],
    },
  },
  'explorer.toolbar.importsRunning': {
    short: 'Open the imports-in-progress panel so you can watch ongoing migrations and see what completed.',
    verified: {
      component: 'src/components/sharepoint/ExplorerWindow.tsx',
      handler: 'setBulkProgressOpen',
      triggers: ['Browser:setState'],
    },
  },

  // ---- 5.5.3 Document Preview action row ------------------------------
  'documentPreview.sendToPromptLab': {
    short: 'Open this document inside Prompt Lab so you can run AI prompts against its full text.',
    long:
      'Send to Prompt Lab attaches this document as the active context, then opens (or focuses) Prompt Lab. The text becomes the subject of the conversation: ask for a summary, a redline, a cite check, or anything else you would normally ask Harvey or CoCounsel.',
    verified: {
      component: 'src/components/sharepoint/DocumentPreview.tsx',
      handler: 'dispatchPromptLabPrefill',
      triggers: [
        'Redux:promptSessions/addSession',
        'Redux:promptSessions/addDocumentContext',
        'Window:open /prompt-lab',
      ],
    },
  },
  'documentPreview.versionHistory': {
    short: 'See every saved version of this document and the time it was changed.',
    long:
      'Version History shows the document\'s timeline. Each save creates a new version — you can compare two versions side by side or restore an older one.',
    verified: {
      component: 'src/components/sharepoint/DocumentPreview.tsx',
      handler: 'setVersionHistoryOpen',
      triggers: ['Browser:setState'],
    },
  },
  'documentPreview.save': {
    short: 'Save your edits back to SharePoint as a new version of this document.',
    long:
      'Save uploads your edits as a new version of the file in SharePoint Online. If another teammate edited the same file in the meantime, you\'ll get a conflict dialog so you can choose to overwrite, keep theirs, or save your edit as a new local copy.',
    verified: {
      component: 'src/components/sharepoint/DocumentPreview.tsx',
      handler: 'handleSave',
      triggers: [
        'Convex:documents.updateExternalEtag',
        'Convex:autoProvisionedPreviewNode.saveAutoProvisionedItem',
        'Browser:graph.put',
      ],
    },
  },
  'documentPreview.saveError.dismiss': {
    short: 'Hide this error and try again — your edits are not lost.',
    verified: {
      component: 'src/components/sharepoint/DocumentPreview.tsx',
      handler: 'setSaveError',
      triggers: ['Browser:setState'],
    },
  },
  // documentPreview.openInSharePoint.disabled — Phase 2 already ships an
  // inline yellow popover with this copy via OpenInSharePointButton +
  // ? chip. Adding it to this registry would duplicate live copy; a
  // future micro-phase will migrate Phase 2's popover to use HelpTip
  // + this key.

  // ---- 5.5.4 Prompt Lab controls --------------------------------------
  'promptLab.modelSelector': {
    short: 'Pick which AI model runs your prompt — Claude Sonnet 4, GPT-4, or a fine-tuned local model.',
    long:
      'Different models have different strengths. Claude Sonnet 4 is the default; switch only when you have a specific reason (e.g. a model your firm has tested on your matter type). Costs vary per model.',
    verified: {
      component: 'src/components/prompt-lab/ModelSelector.tsx',
      handler: 'onSelect',
      triggers: ['Browser:setState'],
    },
  },
  'promptLab.specializationSelector': {
    short: 'Optionally apply a "specialization" — a small adapter trained on a firm Skill to nudge the model toward that style.',
    long:
      'A specialization is a fine-tune created in the Specialization Lab. Picking one routes the run through the inference worker with the specialization\'s adapter loaded. Leave it blank to use the base model only.',
    verified: {
      component: 'src/components/prompt-lab/SpecializationSelector.tsx',
      handler: 'onSelect',
      triggers: ['Browser:setState'],
    },
  },
  'promptLab.runButton': {
    short: 'Send the prompt to the selected model. Ctrl+Enter in the prompt box also runs.',
    long:
      'Run dispatches the prompt with all attached document context + skill prompts + the system prompt. The output streams into the right pane and the run appears as a step in the Timeline below.',
    verified: {
      component: 'src/components/prompt-lab/PromptLabWindow.tsx',
      handler: 'handleRun',
      triggers: [
        'Redux:promptSessions/setSessionStatus',
        'Convex:inferenceJobs.request',
        'Convex:inferenceJobs.kickSampler',
      ],
    },
  },
  'promptLab.stopButton': {
    short: 'Stop the current run. Any partial output captured so far becomes a Timeline step.',
    verified: {
      component: 'src/components/prompt-lab/PromptLabWindow.tsx',
      handler: 'handleStop',
      triggers: ['Redux:promptSessions/setSessionStatus'],
    },
  },
  'promptLab.addSkill': {
    short: 'Insert a saved Skill — a reusable prompt fragment from your firm\'s Skill Library — into this session\'s system prompt.',
    long:
      'Skills are pre-authored prompt fragments your firm shares (e.g. "Cite-check style", "M&A redline rubric"). Adding one prepends its body to the system prompt so every run carries that context.',
    verified: {
      component: 'src/components/prompt-lab/SkillPromptSlots.tsx',
      handler: 'setShowPicker',
      triggers: ['Browser:setState'],
    },
  },

  // ---- 5.5.5 Import Wizard ---------------------------------------------
  'importWizard.cancel': {
    short: 'Close the import wizard without importing anything.',
    verified: {
      component: 'src/components/sharepoint/ImportWizard.tsx',
      handler: 'onCancel',
      triggers: ['Browser:setState'],
    },
  },
  'importWizard.back': {
    short: 'Go back to the previous step in the wizard.',
    verified: {
      component: 'src/components/sharepoint/ImportWizard.tsx',
      handler: 'setStep',
      triggers: ['Browser:setState'],
    },
  },
  'importWizard.next': {
    short: 'Continue to the next step.',
    verified: {
      component: 'src/components/sharepoint/ImportWizard.tsx',
      handler: 'setStep',
      triggers: ['Browser:setState'],
    },
  },
  'importWizard.connectMicrosoft': {
    short: 'Sign in to Microsoft so Repancy can list the SharePoint files you have access to.',
    long:
      'Opens a Microsoft sign-in popup. Repancy uses delegated permissions — it can only see and edit files YOU already have access to in SharePoint, never anyone else\'s. Sign-in lasts for your browser session; you can disconnect at any time from the M365 cell in the status bar.',
    verified: {
      component: 'src/components/sharepoint/ImportWizard.tsx',
      handler: 'handleConnect',
      triggers: ['Browser:msal.loginPopup'],
    },
  },

  // ---- 5.5.6 Onboarding Wizard ----------------------------------------
  'onboard.startSetup': {
    short: 'Begin the guided setup that creates a real SharePoint portal for your firm.',
    long:
      'You\'ll name your firm, optionally connect a Microsoft account, and Repancy will provision a fresh SharePoint Online site with seeded folders (Matters, Clients, Skills, Templates). Takes about 60–120 seconds.',
    verified: {
      component: 'src/components/onboard/OnboardingWizardWindow.tsx',
      handler: 'goToStep',
      triggers: ['Browser:setState'],
    },
  },
  'onboard.advancedMode': {
    short: 'Advanced: use your firm\'s existing Microsoft 365 tenant instead of the Repancy demo workspace.',
    long:
      'Advanced mode walks you through admin consent for your tenant, MSAL sign-in, and then portal provisioning into your own SharePoint Online environment. Choose this only if you have admin rights or know the admin will approve.',
    verified: {
      component: 'src/components/onboard/OnboardingWizardWindow.tsx',
      handler: 'persistMode',
      triggers: ['Browser:setState'],
    },
  },
  'onboard.signinMicrosoft': {
    short: 'Open the Microsoft sign-in popup. Repancy will request the permissions needed to create your portal.',
    long:
      'Sign in with a SharePoint admin or Global Admin account. Repancy requests Files.ReadWrite.All + Sites.ReadWrite.All — the minimum needed to create a new site collection in your tenant.',
    verified: {
      component: 'src/components/onboard/OnboardingWizardWindow.tsx',
      handler: 'handleSignIn',
      triggers: ['Browser:msal.loginPopup'],
    },
  },
  'onboard.firmSubmit': {
    short: 'Continue past your firm details. The next step lets you optionally drop in seed files; then provisioning kicks off.',
    long:
      'Submitting the firm step advances the wizard to the optional "bring your own seed files" step. From there, clicking Provision kicks off SharePoint provisioning — Repancy creates a fresh site, seeds the standard legal folder structure (Matters, Clients, Skills, Templates), and binds it to your account. Total time is usually 60–120 seconds.',
    verified: {
      component: 'src/components/onboard/OnboardingWizardWindow.tsx',
      handler: 'handleFirmSubmit',
      triggers: ['Browser:setState'],
    },
  },

  // ---- 6.5 Public share-link buttons ----------------------------------
  'documentPreview.sharePublicly': {
    short: 'Creates an Anyone-with-the-link URL for this document. View-only, 7-day expiry. Your real Repancy instance would show granular permissions.',
    long:
      'Generates a SharePoint anonymous share-link via Microsoft Graph. Anyone who opens the URL views the doc in SharePoint Online — no sign-in. Useful for demo audiences. The demo workspace is all sample data, so sharing is safe. Your real Repancy instance would show granular permissions (per-user, per-matter, by-role) — not this single-button "anyone with the link" affordance.',
    verified: {
      component: 'src/components/sharepoint/DocumentPreview.tsx',
      handler: 'handleCreatePublicShare',
      triggers: ['Convex:sharePointPublicLinks.createDocumentShareLink'],
    },
  },
  'explorer.matter.sharePublicly': {
    short: 'Creates an Anyone-with-the-link URL for the matter folder. View-only, 7-day expiry. Your real Repancy instance would show granular permissions.',
    long:
      'Generates a SharePoint anonymous share-link for this matter\'s folder via Microsoft Graph. Anyone who opens the URL sees the matter\'s document list in SharePoint Online — no sign-in. Link expires after 7 days. The demo workspace is all sample data. Your real Repancy instance would show granular permissions (per-user, per-matter, by-role) instead of this single-button share.',
    verified: {
      component: 'src/components/sharepoint/DocumentList.tsx',
      handler: 'handleCreateMatterShare',
      triggers: ['Convex:sharePointPublicLinks.createMatterShareLink'],
    },
  },

  // ---- 5.5.7 Bulk Import Wizard mapping policies ----------------------
  'bulkImport.mapping.singleMatter': {
    short: 'All files land in one matter. You\'ll pick that target matter on the next step.',
    long:
      'Use this when you\'re migrating a folder that belongs to a single client/matter — e.g. "Acme Acquisition working files". Each file becomes a document inside the chosen matter, preserving the original filename but flattening the folder structure.',
    verified: {
      component: 'src/components/sharepoint/BulkImportWizard.tsx',
      handler: 'setMappingPolicy',
      triggers: ['Browser:setState'],
    },
  },
  'bulkImport.mapping.folderPerMatter': {
    short: 'Each top-level folder becomes its own matter. Useful when migrating a "Matters" root folder. Coming in v2.',
    verified: {
      component: 'src/components/sharepoint/BulkImportWizard.tsx',
      handler: 'setMappingPolicy',
      triggers: ['Browser:setState'],
    },
  },
} as const satisfies Record<string, HelpCopyEntry>;

/**
 * Type of a registered key. While the registry is empty (during the
 * foundation commit), `keyof` collapses to `never` and breaks every
 * discriminated union that uses `HelpCopyKey`. Falling back to `string`
 * keeps types stable until the first sweep entries land. Once at least
 * one entry exists, `keyof typeof helpCopy` narrows to the literal
 * union and the fallback drops out.
 */
type _RegistryKeys = keyof typeof helpCopy;
export type HelpCopyKey = [_RegistryKeys] extends [never] ? string : _RegistryKeys;

/**
 * Type-safe lookup. Returns `undefined` for unknown keys, which is also
 * a valid TS error at the callsite (the `k` prop on HelpTip is typed
 * as `HelpCopyKey`). The runtime fallback exists for code paths that
 * compute keys dynamically (rare; flagged by ESLint where possible).
 */
export function getHelpCopy(key: string): HelpCopyEntry | undefined {
  return (helpCopy as Record<string, HelpCopyEntry>)[key];
}
